import { Loader, Menu, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
    IconArrowDown,
    IconArrowUp,
    IconDots,
    IconPencil,
    IconPlus,
    IconShield,
    IconTrash,
    IconX,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FirewallVMRuleSingleObject } from '../../../../../@types/firewall-vm-rule';
import { Button, ConfirmActionModal } from '../../../../../components/Atoms';
import DialogBox from '../../../../../components/Atoms/DialogBox/DialogBox';
import { useDeleteFirewallRuleMutate } from '../../../../../hooks/CloudComputeHooks/useDeleteFirewallRuleMutate';
import { useDetachSecurityGroupMutate } from '../../../../../hooks/CloudComputeHooks/useDetachSecurityGroupMutate';
import { useFirewallVMRulesQuery } from '../hooks/useFirewallVMRulesQuery';
import AttachSecurityGroupModal from './AttachSecurityGroupModal';
import FirewallRuleModal from './FirewallRuleModal';

interface FirewallRulesCardProps {
    instanceId: number;
    /** False with view-only access: rules are listed without add, edit, delete or detach. */
    canManage: boolean;
}

const GRID = 'grid grid-cols-[120px_90px_70px_110px_150px_150px_1fr_120px_40px] gap-3 items-center';
// Floor for the scroll container: fixed tracks (850) + gaps (8x12) + px-4 (32) + a 160px floor
// for the description track. Keep in sync with GRID.
const GRID_MIN_W = 'min-w-[1140px]';

interface RowData extends FirewallVMRuleSingleObject {
    group?: { id: string | number; name: string } | null;
}

function DirectionPill({ direction }: { direction: 'in' | 'out' }) {
    const { t } = useTranslation('cloudComputeInstance');
    if (direction === 'in') {
        return (
            <span
                className="inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full text-xs"
                style={{ backgroundColor: '#3b82f626', color: 'var(--color-status-info)' }}
            >
                <IconArrowDown size={12} />
                {t('firewall.direction.inbound')}
            </span>
        );
    }
    return (
        <span
            className="inline-flex w-fit items-center gap-1 px-2 py-1 rounded-full text-xs"
            style={{ backgroundColor: '#a78bfa26', color: 'var(--color-status-violet)' }}
        >
            <IconArrowUp size={12} />
            {t('firewall.direction.outbound')}
        </span>
    );
}

function GroupPill({ name }: { name: string }) {
    return (
        <span className="inline-flex items-center px-2 py-1 rounded-md bg-surface-2 border border-line text-xs text-fg/80 font-mono truncate">
            {name}
        </span>
    );
}

export default function FirewallRulesCard({ instanceId, canManage }: FirewallRulesCardProps) {
    const { t } = useTranslation('cloudComputeInstance');
    const { data, isPending } = useFirewallVMRulesQuery(instanceId);
    const deleteMutation = useDeleteFirewallRuleMutate();
    const detachMutation = useDetachSecurityGroupMutate();

    const [ruleModalOpened, { open: openRuleModal, close: closeRuleModal }] = useDisclosure();
    const [editingRule, setEditingRule] = useState<FirewallVMRuleSingleObject | null>(null);

    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure();
    const [deletingPosition, setDeletingPosition] = useState<number | null>(null);

    const [attachOpened, { open: openAttach, close: closeAttach }] = useDisclosure();
    const [detachOpened, { open: openDetach, close: closeDetach }] = useDisclosure();
    const [detachTarget, setDetachTarget] = useState<{ id: string; name: string } | null>(null);

    const { rows, sgCount, attachedSgs } = useMemo(() => {
        if (!data)
            return {
                rows: [] as RowData[],
                sgCount: 0,
                attachedSgs: [] as Array<{ id: string; name: string }>,
            };
        const sgByPosition = new Map<number, { id: string | number; name: string }>();
        const sgList: Array<{ id: string; name: string }> = [];
        for (const item of data.rules) {
            if (item.type === 'security_group' && item.security_group) {
                const sg = item.security_group;
                for (const pos of sg.positions) {
                    sgByPosition.set(pos, { id: sg.id, name: sg.name });
                }
                sgList.push({ id: String(sg.id), name: sg.name });
            }
        }
        const r = data.rules
            .filter((i) => i.type === 'rule' && i.rule)
            .map<RowData>((i) => ({
                ...i.rule!,
                group: sgByPosition.get(i.rule!.position) ?? null,
            }));
        return { rows: r, sgCount: data.security_group_count, attachedSgs: sgList };
    }, [data]);

    const askDetach = (sg: { id: string; name: string }) => {
        setDetachTarget(sg);
        openDetach();
    };

    const confirmDetach = () => {
        if (!detachTarget) return;
        detachMutation.mutate(
            { id: instanceId, security_group_id: detachTarget.id },
            {
                onSuccess: () => {
                    closeDetach();
                    setDetachTarget(null);
                },
            }
        );
    };

    const atSgLimit = sgCount >= 5;

    const openCreate = () => {
        setEditingRule(null);
        openRuleModal();
    };

    const openEdit = (rule: FirewallVMRuleSingleObject) => {
        setEditingRule(rule);
        openRuleModal();
    };

    const askDelete = (position: number) => {
        setDeletingPosition(position);
        openDelete();
    };

    const confirmDelete = () => {
        if (deletingPosition == null) return;
        deleteMutation.mutate(
            { id: instanceId, position: deletingPosition },
            {
                onSuccess: () => {
                    closeDelete();
                    setDeletingPosition(null);
                },
            }
        );
    };

    const headerActions = (
        <div className="flex flex-row items-center gap-2">
            <Tooltip
                label={t('firewall.attachGroupLimit')}
                disabled={!atSgLimit}
                position="bottom"
                withinPortal
            >
                <Button
                    size="sm"
                    variant="secondary"
                    leftIcon={<IconShield size={16} />}
                    onClick={openAttach}
                    disabled={atSgLimit}
                >
                    {t('firewall.attachGroup')}
                </Button>
            </Tooltip>
            <Button
                size="sm"
                variant="primary"
                leftIcon={<IconPlus size={16} />}
                onClick={openCreate}
            >
                {t('firewall.addRule')}
            </Button>
        </div>
    );

    return (
        <DialogBox
            label={t('firewall.title')}
            description={t('firewall.sgCount', { count: sgCount })}
            rightDescription={canManage && headerActions}
            classNames=""
        >
            {attachedSgs.length > 0 && (
                <div className="w-full px-4 py-3 border-b-[0.5px] border-line flex flex-row flex-wrap items-center gap-2">
                    <span className="text-xs text-fg/68 uppercase tracking-wider mr-1">
                        {t('firewall.attachedGroups')}
                    </span>
                    {attachedSgs.map((sg) => (
                        <span
                            key={sg.id}
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-2 border border-line text-xs text-fg font-mono"
                        >
                            {sg.name}
                            {canManage && (
                                <button
                                    type="button"
                                    onClick={() => askDetach(sg)}
                                    aria-label={t('firewall.detachAria', { name: sg.name })}
                                    className="ml-0.5 text-fg/68 hover:text-fg"
                                >
                                    <IconX size={12} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
            <div
                className="w-full overflow-x-auto"
                tabIndex={0}
                role="region"
                aria-label={t('firewall.title')}
            >
                <div className={GRID_MIN_W}>
                    <div
                        className={`${GRID} w-full px-4 py-3 text-xs text-fg/68 uppercase tracking-wider text-nowrap border-b-[0.5px] border-line`}
                    >
                        <span>{t('firewall.headers.direction')}</span>
                        <span>{t('firewall.headers.protocol')}</span>
                        <span>{t('firewall.headers.port')}</span>
                        <span>{t('firewall.headers.interface')}</span>
                        <span>{t('firewall.headers.source')}</span>
                        <span>{t('firewall.headers.destination')}</span>
                        <span>{t('firewall.headers.description')}</span>
                        <span>{t('firewall.headers.group')}</span>
                        <span />
                    </div>

                    {rows.map((r) => {
                        return (
                            <div
                                key={r.position}
                                className={`${GRID} w-full px-4 py-3 border-t-[0.5px] border-line`}
                            >
                                <DirectionPill direction={r.direction} />
                                <span className="text-sm text-fg uppercase">
                                    {r.protocol ? r.protocol : t('firewall.values.all')}
                                </span>
                                <span className="text-sm text-fg">
                                    {r.destination_port ||
                                        r.source_port ||
                                        t('firewall.values.allPorts')}
                                </span>
                                <span className="text-sm text-fg">
                                    {t(
                                        r.iface === 'net1'
                                            ? 'firewall.interfaceOption.private'
                                            : 'firewall.interfaceOption.public'
                                    )}
                                </span>
                                <span className="text-sm text-fg font-mono truncate">
                                    {r.source || t('firewall.values.defaultCidr')}
                                </span>
                                <span className="text-sm text-fg font-mono truncate">
                                    {r.destination || t('firewall.values.defaultCidr')}
                                </span>
                                <span className="text-sm text-fg/80 truncate">
                                    {r.description || t('firewall.values.emDash')}
                                </span>
                                <div>{r.group ? <GroupPill name={r.group.name} /> : null}</div>
                                {!canManage ? (
                                    <span />
                                ) : (
                                    <Menu position="bottom-end" withinPortal>
                                        <Menu.Target>
                                            <button
                                                type="button"
                                                aria-label={t('firewall.rowActions')}
                                                className="w-8 h-8 inline-flex items-center justify-center rounded-md text-fg/68 hover:bg-surface-3 hover:text-fg transition-colors"
                                            >
                                                <IconDots size={16} />
                                            </button>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Item
                                                leftSection={<IconPencil size={14} />}
                                                onClick={() => openEdit(r)}
                                                disabled={!!r.group}
                                            >
                                                {t('firewall.editRuleAction')}
                                            </Menu.Item>
                                            <Menu.Item
                                                color="var(--color-status-danger)"
                                                leftSection={<IconTrash size={14} />}
                                                onClick={() => askDelete(r.position)}
                                            >
                                                {t('firewall.deleteRuleAction')}
                                            </Menu.Item>
                                        </Menu.Dropdown>
                                    </Menu>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {isPending && !data ? (
                <div className="w-full p-8 flex justify-center">
                    <Loader size={20} color="var(--fg)" />
                </div>
            ) : rows.length === 0 ? (
                <div className="w-full p-8 text-sm text-fg/68 text-center">
                    <Trans
                        i18nKey="firewall.noRules"
                        ns="cloudComputeInstance"
                        components={{ b: <b className="text-fg" /> }}
                    />
                </div>
            ) : null}

            <FirewallRuleModal
                opened={ruleModalOpened}
                onClose={() => {
                    closeRuleModal();
                    setEditingRule(null);
                }}
                instanceId={instanceId}
                rule={editingRule ?? undefined}
            />
            <ConfirmActionModal
                opened={deleteOpened}
                onClose={closeDelete}
                title={t('firewall.deleteRuleTitle')}
                message={t('firewall.deleteRuleMessage')}
                confirmLabel={t('firewall.deleteRule')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
            <AttachSecurityGroupModal
                opened={attachOpened}
                onClose={closeAttach}
                instanceId={instanceId}
                attachedIds={attachedSgs.map((s) => s.id)}
            />
            <ConfirmActionModal
                opened={detachOpened}
                onClose={closeDetach}
                title={t('firewall.detachTitle')}
                message={
                    detachTarget ? t('firewall.detachMessage', { name: detachTarget.name }) : ''
                }
                confirmLabel={t('firewall.detach')}
                confirmVariant="danger"
                isLoading={detachMutation.isPending}
                onConfirm={confirmDetach}
            />
        </DialogBox>
    );
}
